import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RoutesRecognized } from '@angular/router';
import { AsmAuthStorageService, CsAgentAuthService } from '@spartacus/asm/root';
import {
  AuthService,
  AuthStorageService,
  CmsService,
  GlobalMessageService,
  GlobalMessageType,
  Page,
  RoutingService,
} from '@spartacus/core';
import { EMPTY, Subscription, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, switchMap, take, tap } from 'rxjs/operators';
import { AuthRedirectStorageService } from './service/auth/auth-redirect-storage.service';
import { ComponentService } from './service/component.service';
import { GoogleTagManagerService } from './service/googletagmanager/google-tag-manager.service';
import { IconService } from './service/icon/icon.service';
import { TokenIdleService } from './service/idle-session/token-idle.service';
import { StorageService } from './service/storage/storage.service';
import { LogoutService } from './service/user/logout.service';
import { ASM_ENABLED_LOCAL_STORAGE_KEY } from './spartacus/features/asm/asm-constant';

export let browserRefresh = false;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'mystore';
  subscription: Subscription;
  navigateSubscription: Subscription;
  tokenExpiredSubscription$: Subscription;

  constructor(
    public router: Router,
    private iconService: IconService,
    private gtmService: GoogleTagManagerService,
    private location: Location,
    protected routingService: RoutingService,
    protected authService: AuthService,
    private authStorageService: AuthStorageService,
    protected asmAuthService: AsmAuthStorageService,
    private csAgentAuthService: CsAgentAuthService,
    private componentService: ComponentService,
    private cmsService: CmsService,
    private authRedirectStorageService: AuthRedirectStorageService,
    private logoutService: LogoutService,
    private storageService: StorageService,
    private renderer: Renderer2,
    private tokenIdleService: TokenIdleService,
    private globalMessageService: GlobalMessageService
  ) {
    this.subscription = router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        browserRefresh = !router.navigated;
      }
    });
    const params = this.location.path().split('?')[1];
    const tokenParams = params?.includes('token');
    const forgotPasswordPage = this.location.path()?.includes('/forgot-password');

    if (params && params.split('&').includes('asm=false') && !tokenParams && !forgotPasswordPage) {
      this.logoutAsmFeature();
    }
  }

  ngOnInit() {
    this.handleAutoLoggedOutSession();
    this.iconService.registerIcons();
    this.router.events.forEach((item) => {
      if (item instanceof NavigationEnd) {
        this.gtmService.clearGtmSessionStorage();
        const gtmTag = {
          event: 'page',
          pageName: item.url,
        };
        this.gtmService.pushTag(gtmTag);
      }
    });
    // hide page to reload only after login
    this.navigateSubscription = combineLatest([
      this.routingService.isNavigating(),
      this.cmsService.getCurrentPage().pipe(
        filter(Boolean),
        map((currentPage: Page) => currentPage.pageId)
      ),
      this.router.events.pipe(
        filter((event: any) => event instanceof RoutesRecognized),
        pairwise()
      ),
      this.componentService.isPageLoadedFromLogin$,
      this.authRedirectStorageService.getRedirectUrl(),
    ])
      .pipe(
        distinctUntilChanged(),
        debounceTime(0),
        tap(([isNavigating, pageId, events, isPageLoadedFromLogin, redirectUrl]) => {
          if (
            isNavigating &&
            (pageId.includes('login') ||
              events[0].urlAfterRedirects.endsWith('login') ||
              events[0].urlAfterRedirects.endsWith('dealer-selection') ||
              events[0].urlAfterRedirects.endsWith('unit-selection'))
          ) {
            this.hideStorefrontPage('start-navigating', true);
            this.toggleHeaderVisibility(true);
          } else if (
            (isPageLoadedFromLogin && redirectUrl === events[1].urlAfterRedirects) ||
            events[1].urlAfterRedirects.endsWith('login') ||
            events[1].urlAfterRedirects.endsWith('login?asm=true') ||
            events[1].urlAfterRedirects.endsWith('forgot-password') ||
            events[1].urlAfterRedirects.endsWith('dealer-selection')
          ) {
            setTimeout(() => {
              this.hideStorefrontPage('stop-navigating', false);
              this.hideStorefrontPage('start-navigating', false);
              this.toggleHeaderVisibility(false);
            }, 500);
          } else {
            if (!isNavigating) {
              this.hideStorefrontPage('stop-navigating', false);
              this.toggleHeaderVisibility(false);
            }
          }
        })
      )
      .subscribe();
    // Handle the initial state
    setTimeout(() => {
      this.toggleHeaderVisibility(false);
    }, 0);
  }

  hideStorefrontPage(className: string, add: boolean) {
    this.toggleClassName(document.getElementsByClassName(className)[0] as HTMLElement, 'hide-all', add);
  }

  toggleClassName(element: HTMLElement, className: string, add: boolean) {
    if (element) {
      add ? element.classList.add(className) : element.classList.remove(className);
    }
  }

  isLoginRoute() {
    return this.router.url.includes('login');
  }

  ngAfterViewChecked() {
    // Remove tabindex to disable focus
    document.querySelector('cx-storefront').removeAttribute('tabindex');
    document.querySelector('header').removeAttribute('tabindex');
    document.querySelector('main').removeAttribute('tabindex');
    document.querySelector('footer').removeAttribute('tabindex');
  }

  handleAutoLoggedOutSession() {
    const asmEnabled = this.storageService.getLocalStorageItem(ASM_ENABLED_LOCAL_STORAGE_KEY);

    if (!asmEnabled) {
      this.tokenExpiredSubscription$ = this.authService
        .isUserLoggedIn()
        .pipe(
          switchMap((isUserLoggedIn) => {
            if (!isUserLoggedIn) {
              return EMPTY;
            }
            return this.tokenIdleService.userTokenExpiration();
          }),
          filter((isExpired) => isExpired),
          take(1)
        )
        .subscribe({
          next: () => {
            this.authService.coreLogout().finally(() => {
              this.routingService.go({ cxRoute: 'login' });
              this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_CONFIRMATION);
              window.location.reload();
            });
          },
        });
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (this.navigateSubscription) {
      this.navigateSubscription.unsubscribe();
    }

    if (this.tokenExpiredSubscription$) {
      this.tokenExpiredSubscription$.unsubscribe();
      this.tokenIdleService.stopMonitoringToken();
      this.tokenIdleService.resetTokenExpiration();
    }
  }

  private toggleHeaderVisibility(hide: boolean) {
    const header = document.querySelector('header');
    const navigationBar = document.querySelector('cx-page-slot[position="NavigationBar"]');
    if (header) {
      if (hide) {
        this.renderer.setStyle(header, 'display', 'none');
      } else {
        this.renderer.removeStyle(header, 'display');
      }
    }
    if (navigationBar) {
      if (hide) {
        this.renderer.setStyle(navigationBar, 'display', 'none');
      } else {
        this.renderer.removeStyle(navigationBar, 'display');
      }
    }
  }

  logoutAsmFeature() {
    this.authStorageService
      .getToken()
      .pipe(take(1))
      .subscribe((token) => {
        if (this.asmAuthService.getTokenTarget()) {
          this.logoutService.logout();
        }
        this.csAgentAuthService.logoutCustomerSupportAgent();
      });
    if (this.storageService) {
      this.storageService.removeLocalStorageItem(ASM_ENABLED_LOCAL_STORAGE_KEY);
    }
  }
}
